import { Link } from "@inertiajs/react"

interface DocumentType {
  id: number
  name: string
}

export interface DocumentFormData {
  bg_document_type_id: number | null
  title: string
  notes: string
  document_date: string
  significant: number
  on_reports: number
  is_public: number
  files?: File[] | null
}

interface Props {
  caseId: number
  data: DocumentFormData
  setData: (
    key: keyof DocumentFormData,
    value: string | number | File[] | null,
  ) => void
  errors?: Record<string, string[]>
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  submitLabel: string
  documentTypes: DocumentType[]
  isNew?: boolean
  currentFilename?: string | null
}

export default function DocumentForm({
  caseId,
  data,
  setData,
  errors,
  processing,
  onSubmit,
  submitLabel,
  documentTypes,
  isNew = false,
  currentFilename,
}: Props) {
  return (
    <form onSubmit={onSubmit} className="max-w-3xl">
      {isNew && (
        <div className="mb-4">
          <label htmlFor="files" className="mb-1 block font-medium">
            Files <span className="text-hpi-red">*</span>
          </label>
          <input
            type="file"
            id="files"
            multiple
            onChange={(e) =>
              setData(
                "files",
                e.target.files ? Array.from(e.target.files) : null,
              )
            }
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          />
          {errors?.files && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.files.join(", ")}
            </p>
          )}
        </div>
      )}

      {!isNew && currentFilename && (
        <div className="mb-4">
          <label className="mb-1 block font-medium">Current File</label>
          <p className="text-hpi-dark-gray">{currentFilename}</p>
        </div>
      )}

      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <div>
          <label
            htmlFor="bg_document_type_id"
            className="mb-1 block font-medium"
          >
            Document Type
          </label>
          <select
            id="bg_document_type_id"
            value={data.bg_document_type_id ?? ""}
            onChange={(e) =>
              setData(
                "bg_document_type_id",
                e.target.value ? Number(e.target.value) : null,
              )
            }
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          >
            <option value="">-- Select --</option>
            {documentTypes.map((dt) => (
              <option key={dt.id} value={dt.id}>
                {dt.name}
              </option>
            ))}
          </select>
          {errors?.bg_document_type_id && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.bg_document_type_id.join(", ")}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="document_date" className="mb-1 block font-medium">
            Document Date
          </label>
          <input
            type="datetime-local"
            id="document_date"
            value={data.document_date ? data.document_date.slice(0, 16) : ""}
            onChange={(e) => setData("document_date", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          />
          {errors?.document_date && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.document_date.join(", ")}
            </p>
          )}
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="title" className="mb-1 block font-medium">
          Title
        </label>
        <input
          type="text"
          id="title"
          value={data.title}
          onChange={(e) => setData("title", e.target.value)}
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
        />
        {errors?.title && (
          <p className="text-hpi-red mt-1 text-sm">{errors.title.join(", ")}</p>
        )}
      </div>

      <div className="mb-4">
        <label htmlFor="notes" className="mb-1 block font-medium">
          Notes
        </label>
        <textarea
          id="notes"
          value={data.notes}
          onChange={(e) => setData("notes", e.target.value)}
          className="border-hpi-light-gray w-full rounded border px-3 py-2"
          rows={4}
        />
        {errors?.notes && (
          <p className="text-hpi-red mt-1 text-sm">{errors.notes.join(", ")}</p>
        )}
      </div>

      <div className="mb-6 grid gap-3 md:grid-cols-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={data.significant === 1}
            onChange={(e) => setData("significant", e.target.checked ? 1 : 0)}
            className="border-hpi-light-gray rounded"
          />
          <span className="font-medium">Significant</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={data.on_reports === 1}
            onChange={(e) => setData("on_reports", e.target.checked ? 1 : 0)}
            className="border-hpi-light-gray rounded"
          />
          <span className="font-medium">On Reports</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={data.is_public === 1}
            onChange={(e) => setData("is_public", e.target.checked ? 1 : 0)}
            className="border-hpi-light-gray rounded"
          />
          <span className="font-medium">Public</span>
        </label>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {submitLabel}
        </button>
        <Link
          href={`/cases/${caseId}/documents`}
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}
